import { engineIdOf } from '@bt-common/engine/identity';
import { normalizeSenderPhone, type EngineSenderClaim } from '@bt-common/engine/sender-binding';

/**
 * #922 S0b (§2.4): which engine instance, and which number, is asking for a send slot.
 *
 * - Chrome gateway (has a `sessionId`): `session:<id>` only. The server reads the number off the
 *   sessions doc and does not trust a self-report, so none is sent.
 * - Browser extension: `client:<mqttClientId>` plus its own WhatsApp number (`waMeUser`), normalized
 *   to E.164 digits. Anything that is not a phone (an `@lid`, a group, junk) is left out, never guessed.
 *
 * Any missing input just drops its field. An empty claim is the pre-S0b request, byte for byte.
 */
export function buildEngineSenderClaim(input: {
    gatewaySessionId?: string | null;
    mqttClientId?: string | null;
    waMeUser?: string | null;
}): EngineSenderClaim {
    if (input.gatewaySessionId) return { engineId: engineIdOf({ source: 'session', sessionId: input.gatewaySessionId }) };
    const claim: EngineSenderClaim = {};
    if (input.mqttClientId) claim.engineId = engineIdOf({ source: 'client', clientId: input.mqttClientId });
    const phone = input.waMeUser ? normalizeSenderPhone(input.waMeUser) : null;
    if (phone) claim.senderPhone = phone;
    return claim;
}

/** The gateway's own session id (the same `localStorage.sessionId` `getClientType` keys on). */
export function readGatewaySessionId(): string | null {
    return typeof localStorage !== 'undefined' ? localStorage.getItem('sessionId') : null;
}
